// React Router bridge for the BARK SPA. (BAR-40)
//
// The URL is the single source of truth for top-level navigation. This module
// splits that into two concerns:
//   - useActiveView(): a READ-ONLY projection of the URL (current view + its
//     project id / filters / quick-query), derived from useLocation. Used by
//     the shell chrome for nav highlighting and by view routes to read their
//     own URL params. It is NOT a state machine — nothing dispatches on it; the
//     route tree (ShellRoutes) selects the view.
//   - useNavigateToView(): the single navigation WRITER (wraps navStateToPath +
//     an idempotency guard). The shell exposes it on ShellUiContext, so every
//     view calls the same canonicalizing writer via useShellUiActions().
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProjectStore } from '../stores/projectStore';
import {
  MainView,
  NavState,
  ProjectType,
  ProjectsFilter,
  isProjectScopedView,
  navStateToPath,
  pathToNavState,
} from './routes';

export interface NavigateToViewOptions {
  /** Explicit project id for a project-scoped view. When omitted, the
   *  currently-active project (from the store) is used. */
  projectId?: string;
  projectsFilter?: ProjectsFilter;
  newProjectType?: ProjectType;
  quickQuery?: string;
  classificationSelection?: string;
  classificationShare?: string;
  /** Replace the current history entry instead of pushing a new one. */
  replace?: boolean;
}

export type NavigateToView = (view: MainView, opts?: NavigateToViewOptions) => void;

export interface ActiveView {
  /** Current top-level view, derived from the URL. */
  view: MainView;
  /** Active project id from the URL (project-scoped views only). */
  projectId?: string;
  /** `/projects` filter (defaults to 'all'). */
  projectsFilter: ProjectsFilter;
  /** `/projects/new` form variant (defaults to 'research'). */
  newProjectType: ProjectType;
  /** `/quick-research` prefill query (null when absent). */
  quickQuery: string | null;
  /** The raw parsed NavState (for callers that need everything). */
  current: NavState;
}

/** Read-only URL projection (see module header). */
export function useActiveView(): ActiveView {
  const location = useLocation();
  const current = useMemo(
    () => pathToNavState(location.pathname, location.search),
    [location.pathname, location.search],
  );
  return {
    view: current.view,
    projectId: current.projectId,
    projectsFilter: current.projectsFilter ?? 'all',
    newProjectType: current.newProjectType ?? 'research',
    quickQuery: current.quickQuery ?? null,
    current,
  };
}

/** The single navigation writer (see module header). */
export function useNavigateToView(): NavigateToView {
  const navigate = useNavigate();

  return useCallback(
    (view: MainView, opts: NavigateToViewOptions = {}) => {
      let projectId = opts.projectId;
      // Project-scoped views default to the currently-active project so
      // callers like `navigateToView('workspace')` Just Work right after a
      // project has been loaded into the store. This is a CONVENIENCE fallback,
      // not a contract: callers that navigate to a SPECIFIC project (open from
      // a list, create, deep-link) MUST pass `projectId` explicitly — relying
      // on the live-store fallback there would race the store's activeProject.
      // (All current project-scoped call sites do pass it explicitly.)
      if (!projectId && isProjectScopedView(view)) {
        projectId = useProjectStore.getState().activeProject?.project_id || undefined;
      }

      const target = navStateToPath({
        view,
        projectId,
        projectsFilter: opts.projectsFilter,
        newProjectType: opts.newProjectType,
        quickQuery: opts.quickQuery,
        classificationSelection: opts.classificationSelection,
        classificationShare: opts.classificationShare,
      });

      // Idempotency guard (read the LIVE url, not a closed-over location):
      // navigating to the path we're already on would push a duplicate history
      // entry and, during project-load hydration, could ping-pong. Compare
      // CANONICAL forms so a non-canonical current URL (e.g. a hand-typed
      // `?type=all`, which navStateToPath omits) doesn't trigger a spurious
      // no-op navigation. window.location is always current.
      // NOTE: intentionally window.location, NOT useLocation() — useLocation
      // can be stale within the same render (a set-then-navigate in one
      // handler). Trade-off: this guard is inert under MemoryRouter in tests,
      // which is fine (the guard is a history-hygiene optimization, not logic).
      const currentCanonical = navStateToPath(
        pathToNavState(window.location.pathname, window.location.search),
      );
      if (target === currentCanonical) return;

      navigate(target, { replace: !!opts.replace });
    },
    [navigate],
  );
}
